import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMemo } from 'react';

import {
  DOMAIN_LABELS,
  featureFlagRegistry,
  groupFlagsByDomain,
} from './registry';
import { FeatureFlagGlobalRow } from './FeatureFlagGlobalRow';
import { FeatureFlagDomain, FlagEntry } from './types';

type Panel = {
  domain: FeatureFlagDomain;
  entries: FlagEntry[];
  proposedCount: number;
  matureCount: number;
};

const buildPanels = (): Panel[] => {
  const grouped = groupFlagsByDomain(featureFlagRegistry);

  const panels: Panel[] = grouped.map(({ domain, entries }) => {
    const proposed = entries.filter(e => e.stability === 'proposed');
    const mature = entries.filter(e => e.stability === 'mature');
    return {
      domain,
      entries: [...proposed, ...mature],
      proposedCount: proposed.length,
      matureCount: mature.length,
    };
  });

  return [...panels].sort((a, b) => {
    const aActive = a.proposedCount > 0 ? 1 : 0;
    const bActive = b.proposedCount > 0 ? 1 : 0;
    return bActive - aActive;
  });
};

const summarySx = {
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper': {
    transform: 'rotate(-90deg)',
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(0deg)',
  },
  '& .MuiAccordionSummary-content': {
    alignItems: 'center',
    gap: 1,
    marginLeft: '0.5em',
  },
} as const;

const detailsSx = {
  backgroundColor: 'rgba(255, 255, 255, 0.03)',
  padding: '1rem',
} as const;

const PanelSection = ({ panel }: { panel: Panel }) => {
  const fullyMature = panel.proposedCount === 0;
  const totalFlags = panel.entries.length;

  return (
    <Accordion defaultExpanded={!fullyMature} disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
        <Typography variant="subtitle1" fontWeight={600}>
          {DOMAIN_LABELS[panel.domain]}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          color={fullyMature ? 'default' : 'primary'}
          label={`${totalFlags} flag${totalFlags === 1 ? '' : 's'}`}
        />
        {fullyMature ? (
          <Chip
            size="small"
            variant="outlined"
            color="default"
            label="All mature"
          />
        ) : null}
      </AccordionSummary>
      <AccordionDetails sx={detailsSx}>
        <Box display="flex" flexDirection="column" gap="1.75rem">
          {panel.entries.map(entry => (
            <FeatureFlagGlobalRow key={entry.key} entry={entry} />
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export const GlobalFeatureFlagsPanel = () => {
  const panels = useMemo(buildPanels, []);

  return (
    <Box display="flex" flexDirection="column">
      {panels.map(panel => (
        <PanelSection key={panel.domain} panel={panel} />
      ))}
    </Box>
  );
};
